// API service for Bond Collective Operations Platform

// Fix for environment variable loading issue
const getApiBaseUrl = () => {
  // Try multiple ways to get the environment variable
  let baseUrl = process.env.REACT_APP_BACKEND_URL || 
                'https://bondhub.io'; // fallback to production URL
  
  // Remove any trailing /api to prevent double /api
  if (baseUrl && baseUrl.endsWith('/api')) {
    baseUrl = baseUrl.slice(0, -4);
  }
  
  console.log('Environment variable sources:', {
    processEnv: process.env.REACT_APP_BACKEND_URL,
    final: baseUrl
  });
  
  return baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    // Ensure we construct the URL correctly and avoid double /api
    this.baseURL = `${API_BASE_URL}/api`;
    console.log('API Service initialized with baseURL:', this.baseURL);
  }

  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    console.log('Making API request to:', url);
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, redirect to login
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Unauthorized');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Authentication
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  // Locations
  async getLocations() {
    return this.request('/locations');
  }

  async getLocation(id) {
    return this.request(`/locations/${id}`);
  }

  async createLocation(locationData) {
    return this.request('/locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  // Offices
  async getOffices(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All Locations') {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/offices${query}`);
  }

  async getOffice(id) {
    return this.request(`/offices/${id}`);
  }

  async createOffice(officeData) {
    return this.request('/offices', {
      method: 'POST',
      body: JSON.stringify(officeData)
    });
  }

  async updateOffice(id, officeData) {
    return this.request(`/offices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(officeData)
    });
  }

  async updateOfficeStatus(id, status) {
    return this.request(`/offices/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async addOfficeContact(id, contactData) {
    return this.request(`/offices/${id}/contacts`, {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  async removeOfficeContact(officeId, contactId) {
    return this.request(`/offices/${officeId}/contacts/${contactId}`, {
      method: 'DELETE'
    });
  }

  // Members
  async getMembers(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All Locations') {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/members${query}`);
  }

  async getMember(id) {
    return this.request(`/members/${id}`);
  }

  async createMember(memberData) {
    return this.request('/members', {
      method: 'POST',
      body: JSON.stringify(memberData)
    });
  }

  async updateMember(id, memberData) {
    return this.request(`/members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(memberData)
    });
  }

  async addMemberEmployee(id, employeeData) {
    return this.request(`/members/${id}/employees`, {
      method: 'POST',
      body: JSON.stringify(employeeData)
    });
  }

  async assignMemberToOffice(memberId, officeId) {
    return this.request(`/members/${memberId}/office`, {
      method: 'PATCH',
      body: JSON.stringify({ office_id: officeId })
    });
  }

  // Tasks
  async getTasks(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All Locations') {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/tasks${query}`);
  }

  async getTask(id) {
    return this.request(`/tasks/${id}`);
  }

  async createTask(taskData) {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  }

  async updateTask(id, taskData) {
    return this.request(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(taskData)
    });
  }

  async updateTaskStatus(id, status) {
    return this.request(`/tasks/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // Reports
  async getOccupancyReport(location = null) {
    const params = location && location !== 'All Locations' 
      ? `?location=${encodeURIComponent(location)}` 
      : '';
    return this.request(`/reports/occupancy${params}`);
  }

  async getTermAnalysisReport(location = null) {
    const params = location && location !== 'All Locations' 
      ? `?location=${encodeURIComponent(location)}` 
      : '';
    return this.request(`/reports/term-analysis${params}`);
  }

  // CRM / Deals
  async getDeals(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All Locations' && value !== 'all') {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/deals${query}`);
  }

  async getDeal(id) {
    return this.request(`/deals/${id}`);
  }

  async createDeal(dealData) {
    return this.request('/deals', {
      method: 'POST',
      body: JSON.stringify(dealData)
    });
  }

  async updateDeal(id, dealData) {
    return this.request(`/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dealData)
    });
  }

  async updateDealStage(id, stage) {
    return this.request(`/deals/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage })
    });
  }

  async generateAgreement(id) {
    return this.request(`/deals/${id}/generate-agreement`, {
      method: 'POST'
    });
  }

  async sendForSignature(id) {
    return this.request(`/deals/${id}/send-signature`, {
      method: 'POST'
    });
  }

  async getPipelineStats(location = null) {
    const params = location && location !== 'All Locations' 
      ? `?location=${encodeURIComponent(location)}` 
      : '';
    return this.request(`/deals/pipeline-stats${params}`);
  }

  // CRM Contacts
  async getCRMContacts(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/crm/contacts${query}`);
  }

  async getCRMContact(id) {
    return this.request(`/crm/contacts/${id}`);
  }

  async createCRMContact(contactData) {
    return this.request('/crm/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  async updateCRMContact(id, contactData) {
    return this.request(`/crm/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contactData)
    });
  }

  async deleteCRMContact(id) {
    return this.request(`/crm/contacts/${id}`, {
      method: 'DELETE'
    });
  }

  async updateCRMContactStatus(id, status) {
    return this.request(`/crm/contacts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // CRM Companies
  async getCRMCompanies(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/crm/companies${query}`);
  }

  async getCRMCompany(id) {
    return this.request(`/crm/companies/${id}`);
  }

  async createCRMCompany(companyData) {
    return this.request('/crm/companies', {
      method: 'POST',
      body: JSON.stringify(companyData)
    });
  }

  async updateCRMCompany(id, companyData) {
    return this.request(`/crm/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(companyData)
    });
  }

  async deleteCRMCompany(id) {
    return this.request(`/crm/companies/${id}`, {
      method: 'DELETE'
    });
  }

  async updateCRMCompanyStatus(id, status) {
    return this.request(`/crm/companies/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // Gmail Integration - Will be rebuilt
  // Removed complex Gmail methods for clean rebuild

  // User Management (Admin Only)
  async getUsers() {
    return this.request('/users');
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE'
    });
  }
}

export default new ApiService();